import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { fonts } from '@/shared/config/fonts'
import { getCookie, removeCookie, setCookie } from '@/shared/lib/cookies'

type Font = (typeof fonts)[number]

const FONT_COOKIE_NAME = 'font'
const FONT_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

type FontContextType = {
  font: Font
  setFont: (font: Font) => void
  resetFont: () => void
}

const FontContext = createContext<FontContextType | null>(null)

export function FontProvider({ children }: { children: ReactNode }) {
  const [font, setFontState] = useState<Font>(() => {
    const savedFont = getCookie(FONT_COOKIE_NAME)
    return fonts.includes(savedFont as Font) ? (savedFont as Font) : fonts[0]
  })

  useEffect(() => {
    const applyFont = (fontName: string) => {
      const root = document.documentElement
      root.classList.forEach((cls) => {
        if (cls.startsWith('font-')) {
          root.classList.remove(cls)
        }
      })
      root.classList.add(`font-${fontName}`)
    }

    applyFont(font)
  }, [font])

  const setFont = (nextFont: Font) => {
    setCookie(FONT_COOKIE_NAME, nextFont, FONT_COOKIE_MAX_AGE)
    setFontState(nextFont)
  }

  const resetFont = () => {
    removeCookie(FONT_COOKIE_NAME)
    setFontState(fonts[0])
  }

  return (
    <FontContext.Provider value={{ font, setFont, resetFont }}>
      {children}
    </FontContext.Provider>
  )
}

export const useFont = () => {
  const context = useContext(FontContext)
  if (!context) {
    throw new Error('useFont must be used within a FontProvider')
  }
  return context
}
